/**
 * SiteContactEditDrawer
 *
 * Slide-in drawer for creating or editing a site contact. Admin-only —
 * the caller in SiteContactsTable gates visibility.
 *
 * Issue: djin-cloud#64
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createSiteContact,
  updateSiteContact,
  type SiteContact,
  type CreateSiteContactData,
} from '../../services/adminApi';

interface Props {
  siteId: string;
  contact: SiteContact | null;
  mode: 'create' | 'edit';
  onClose: () => void;
}

export function SiteContactEditDrawer({ siteId, contact, mode, onClose }: Props) {
  const [name, setName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [message, setMessage] = useState('');

  const queryClient = useQueryClient();

  useEffect(() => {
    if (mode === 'edit' && contact) {
      setName(contact.name);
      setRoleTitle(contact.role_title ?? '');
      setPhone(contact.phone ?? '');
      setEmail(contact.email ?? '');
      setNotes(contact.notes ?? '');
      setSortOrder(contact.sort_order);
    } else {
      setName('');
      setRoleTitle('');
      setPhone('');
      setEmail('');
      setNotes('');
      setSortOrder(0);
    }
    setMessage('');
  }, [contact, mode]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: CreateSiteContactData = {
        name: name.trim(),
        role_title: roleTitle.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
        sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
      };
      if (mode === 'create') {
        return createSiteContact(siteId, payload);
      }
      if (!contact) throw new Error('No contact to update');
      return updateSiteContact(siteId, contact.contact_id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-contacts', siteId] });
      onClose();
    },
    onError: (err: any) => {
      setMessage(err?.response?.data?.error ?? 'Failed to save contact.');
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      setMessage('Name is required.');
      return;
    }
    saveMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20">
      <div className="w-full max-w-md bg-ems-bg-card h-full border-l border-ems-border overflow-y-auto animate-slide-in-right">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ems-border sticky top-0 bg-ems-bg-card z-10">
          <h2 className="text-lg font-semibold text-ems-text-primary">
            {mode === 'create' ? 'New Contact' : 'Edit Contact'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-ems-bg-card-hover text-ems-text-muted"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {message && (
            <div className="p-3 rounded-lg bg-ems-red/5 border border-ems-red/20 text-ems-red text-sm">
              {message}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
              placeholder="e.g. Jane Ops"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">Role</label>
            <input
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
              placeholder="e.g. Operations Lead, Vendor Escalation"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Phone</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
                placeholder="+1-555-0100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
                placeholder="name@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green resize-y"
              placeholder="On-call window, escalation path, preferred channel…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ems-text-primary mb-1">
              Display order
            </label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              className="w-32 px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
            />
            <p className="text-xs text-ems-text-muted mt-1">
              Lower numbers appear first. Ties break alphabetically.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-ems-text-secondary hover:text-ems-text-primary"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="px-4 py-2 rounded-lg bg-ems-green text-white text-sm font-medium disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
